import { useState, useEffect, useCallback } from 'react';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Star, Trophy, Settings, Check, X, Gift, Sparkles, ChevronRight, Upload, ImagePlus } from 'lucide-react';
import { BACKEND_URL } from '@/utils/api';

const THEME_PREVIEWS = {
  stars:   { emoji: '⭐🌟💫✨🏆', label: 'Stars' },
  cars:    { emoji: '🚗🚙🏎️🚀🏆', label: 'Cars' },
  animals: { emoji: '🐟🐬🦈🐋🏆', label: 'Animals' },
  rockets: { emoji: '🛸🚁✈️🚀🏆', label: 'Rockets' },
  ocean:   { emoji: '🫧🐠🐙🦑🏆', label: 'Ocean' },
  custom:  { emoji: '🖼️📸🎨✏️🏆', label: 'Custom Photos' },
};

export function TokenBoard({ childId, childName }) {
  const { user } = useAuth();
  const isInstructor = user?.role === 'instructor';
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [themes, setThemes] = useState({});

  // Setup form
  const [setupTheme, setSetupTheme] = useState('stars');
  const [setupUsePhases, setSetupUsePhases] = useState(true);
  const [setupStreak, setSetupStreak] = useState(7);
  const [setupPrize, setSetupPrize] = useState('');

  const fetchProgram = useCallback(async () => {
    try {
      const res = await api.get(`/token-economy/${childId}`);
      setProgram(res.data);
      if (res.data.theme) setSetupTheme(res.data.theme);
      if (res.data.prize_description) setSetupPrize(res.data.prize_description);
      if (res.data.streak_target) setSetupStreak(res.data.streak_target);
      if (res.data.use_phases !== undefined) setSetupUsePhases(res.data.use_phases);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [childId]);

  useEffect(() => { fetchProgram(); }, [fetchProgram]);

  useEffect(() => {
    api.get('/token-economy/themes').then(r => setThemes(r.data)).catch(() => {});
  }, []);

  const saveSetup = async () => {
    try {
      await api.post(`/token-economy/${childId}/setup`, {
        theme: setupTheme,
        use_phases: setupUsePhases,
        streak_target: setupStreak,
        prize_description: setupPrize,
      });
      toast.success('Token program saved!');
      setShowSetup(false);
      fetchProgram();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  if (loading) return null;

  // Not enabled — show setup prompt for instructors
  if (!program?.enabled) {
    if (!isInstructor) return null;
    return (
      <Card className="rounded-3xl border-dashed border-2 border-amber-200 bg-amber-50/30 mb-6" data-testid="token-setup-prompt">
        <CardContent className="p-6 text-center">
          <Trophy className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <p className="font-bold text-slate-700 mb-1" style={{ fontFamily: 'Nunito' }}>Token Economy Program</p>
          <p className="text-xs text-slate-500 mb-4">Motivate {childName} with tokens and prizes for great sessions</p>
          <Button onClick={() => setShowSetup(true)} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold gap-2" data-testid="enable-token-program">
            <Sparkles className="w-4 h-4" /> Set Up Token Program
          </Button>
          <SetupDialog open={showSetup} onOpenChange={setShowSetup} themes={themes} setupTheme={setupTheme} setSetupTheme={setSetupTheme} setupUsePhases={setupUsePhases} setSetupUsePhases={setSetupUsePhases} setupStreak={setupStreak} setSetupStreak={setSetupStreak} setupPrize={setupPrize} setSetupPrize={setSetupPrize} onSave={saveSetup} childName={childName} childId={childId} />
        </CardContent>
      </Card>
    );
  }

  // Program enabled — show token board
  const themeData = themes[program.theme] || themes.stars || { levels: ['⭐','🌟','💫','✨','🏆'], colors: ['#FCD34D','#F59E0B','#D97706','#B45309','#92400E'] };
  const customImages = program.custom_images || [];
  const tokens = program.tokens || [];
  const currentPhase = program.current_phase || {};
  const prizes = program.prizes || [];

  const getTokenVisual = (level) => {
    const ci = customImages.find(c => c.tier === level);
    if (ci) return { type: 'image', src: `${BACKEND_URL}/api/uploads/${ci.filename}` };
    return { type: 'emoji', emoji: themeData.levels?.[level] || '⭐' };
  };

  // Calculate which level token to show based on streak
  const streakLevel = Math.min(Math.floor((currentPhase.streak || 0) / Math.max(1, Math.ceil((currentPhase.target || 7) / 5))), 4);
  const currentEmoji = themeData.levels?.[streakLevel] || '⭐';
  const currentColor = themeData.colors?.[streakLevel] || '#FCD34D';

  return (
    <>
      <Card className="rounded-3xl border-slate-100 shadow-sm mb-6 overflow-hidden" data-testid="token-board">
        {/* Header bar */}
        <div className="px-5 py-3 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${currentColor}22, ${currentColor}11)` }}>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{currentEmoji}</span>
            <div>
              <p className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>Token Board</p>
              <p className="text-[10px] text-slate-500">
                {program.use_phases ? `${currentPhase.phase_name || 'Phase'} — ${currentPhase.awarded || 0}/${currentPhase.target || 0} tokens` : `Streak: ${currentPhase.streak || 0}/${program.streak_target || 7}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {prizes.length > 0 && (
              <Badge className="rounded-full bg-amber-100 text-amber-700 border-amber-300 gap-1">
                <Gift className="w-3 h-3" /> {prizes.length} prize{prizes.length !== 1 ? 's' : ''}
              </Badge>
            )}
            {isInstructor && (
              <button onClick={() => setShowSetup(true)} className="w-7 h-7 rounded-full bg-white/60 flex items-center justify-center hover:bg-white transition-colors" data-testid="token-settings-btn">
                <Settings className="w-3.5 h-3.5 text-slate-500" />
              </button>
            )}
          </div>
        </div>

        <CardContent className="p-5">
          {/* Token streak visualization */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="token-streak">
            {Array.from({ length: currentPhase.target || 7 }, (_, i) => {
              const token = tokens.find(t => t.session_number === (currentPhase.phase ? (currentPhase.phase - 1) * (currentPhase.target || 7) : 0) + i + 1);
              const isAwarded = token?.awarded;
              const isMissed = token && !token.awarded;
              const level = Math.min(Math.floor(i / Math.max(1, Math.ceil((currentPhase.target || 7) / 5))), 4);
              const visual = getTokenVisual(level);

              return (
                <div
                  key={i}
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center border-2 transition-all overflow-hidden ${
                    isAwarded ? 'border-amber-300 bg-amber-50 shadow-sm scale-105' :
                    isMissed ? 'border-red-200 bg-red-50' :
                    'border-slate-200 bg-slate-50'
                  }`}
                  data-testid={`token-slot-${i}`}
                >
                  {isAwarded ? (
                    visual.type === 'image'
                      ? <img src={visual.src} alt="" className="w-full h-full object-cover animate-fadeIn" />
                      : <span className="text-xl animate-fadeIn">{visual.emoji}</span>
                  ) : isMissed ? (
                    <X className="w-5 h-5 text-red-300" />
                  ) : (
                    visual.type === 'image'
                      ? <img src={visual.src} alt="" className="w-full h-full object-cover opacity-20" />
                      : <span className="text-lg opacity-20">{visual.emoji}</span>
                  )}
                </div>
              );
            })}
            {/* Prize at the end */}
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border-2 ${
              currentPhase.prize_eligible ? 'border-amber-400 bg-amber-100 animate-pulse' : 'border-dashed border-slate-300 bg-white'
            }`}>
              <Trophy className={`w-6 h-6 ${currentPhase.prize_eligible ? 'text-amber-500' : 'text-slate-300'}`} />
            </div>
          </div>

          {/* Prize description */}
          {program.prize_description && (
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-3 border border-amber-200/50 text-center">
              <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Prize</p>
              <p className="text-sm font-bold text-amber-800">{program.prize_description}</p>
            </div>
          )}

          {/* Recent prizes earned */}
          {prizes.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Prizes Earned</p>
              {prizes.slice(0, 3).map(p => (
                <div key={p.prize_id} className="flex items-center gap-2 bg-green-50 rounded-xl p-2.5 border border-green-200">
                  <Gift className="w-4 h-4 text-green-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-green-800 truncate">{p.prize_description}</p>
                    <p className="text-[10px] text-green-600">{p.phase_name} — {p.tokens_collected} tokens</p>
                  </div>
                  <Check className="w-4 h-4 text-green-400 shrink-0" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <SetupDialog open={showSetup} onOpenChange={setShowSetup} themes={themes} setupTheme={setupTheme} setSetupTheme={setSetupTheme} setupUsePhases={setupUsePhases} setSetupUsePhases={setSetupUsePhases} setupStreak={setupStreak} setSetupStreak={setSetupStreak} setupPrize={setupPrize} setSetupPrize={setSetupPrize} onSave={saveSetup} childName={childName} childId={childId} />
    </>
  );
}


export function TokenAwardPrompt({ childId, sessionId, sessionNumber, onComplete }) {
  const [themes, setThemes] = useState({});
  const [program, setProgram] = useState(null);
  const [awarding, setAwarding] = useState(false);
  const [note, setNote] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/token-economy/${childId}`).then(r => setProgram(r.data)).catch(() => {});
    api.get('/token-economy/themes').then(r => setThemes(r.data)).catch(() => {});
  }, [childId]);

  if (!program?.enabled) return null;

  const themeData = themes[program.theme] || { levels: ['⭐','🌟','💫','✨','🏆'] };
  const emoji = themeData.levels?.[2] || '⭐';

  const handleAward = async (awarded) => {
    setAwarding(true);
    try {
      const res = await api.post(`/token-economy/${childId}/award`, {
        session_id: sessionId,
        session_number: sessionNumber,
        awarded,
        note,
      });
      setResult(res.data);
      if (awarded) {
        toast.success(res.data.prize_earned ? `Token awarded + Prize earned!` : 'Token awarded!');
      } else {
        toast.info('Token skipped for this session');
      }
      setTimeout(() => onComplete?.(), 2000);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setAwarding(false); }
  };

  if (result) {
    return (
      <Card className="rounded-3xl border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 shadow-lg mb-6 overflow-hidden" data-testid="token-result">
        <CardContent className="p-8 text-center">
          {result.awarded ? (
            <>
              <div className="text-6xl mb-4 animate-starBounce">{result.prize_earned ? '🏆' : emoji}</div>
              <p className="text-xl font-extrabold text-amber-800" style={{ fontFamily: 'Nunito' }}>
                {result.prize_earned ? 'Prize Earned!' : 'Token Earned!'}
              </p>
              {result.prize_earned && (
                <p className="text-sm text-amber-600 mt-2 font-semibold">
                  <Gift className="w-4 h-4 inline mr-1" />
                  {result.prize?.prize_description || 'Amazing job!'}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="text-4xl mb-4">😔</p>
              <p className="text-lg font-bold text-slate-600">No token this time</p>
              <p className="text-sm text-slate-400 mt-1">Keep trying next session!</p>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-3xl border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 shadow-sm mb-6" data-testid="token-award-prompt">
      <CardContent className="p-6 text-center">
        <span className="text-5xl block mb-3">{emoji}</span>
        <p className="text-lg font-extrabold text-slate-800 mb-1" style={{ fontFamily: 'Nunito' }}>Award a Token?</p>
        <p className="text-sm text-slate-500 mb-4">Did the student do well this session?</p>

        <Input
          placeholder="Optional note (e.g., 'Great focus today!')"
          value={note}
          onChange={e => setNote(e.target.value)}
          className="rounded-xl mb-4 text-sm"
          data-testid="token-note-input"
        />

        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={() => handleAward(true)}
            disabled={awarding}
            className="rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold h-12 gap-2 text-base"
            data-testid="award-token-btn"
          >
            <Star className="w-5 h-5" /> Award Token
          </Button>
          <Button
            onClick={() => handleAward(false)}
            disabled={awarding}
            variant="outline"
            className="rounded-full border-slate-300 text-slate-500 font-bold h-12 gap-2"
            data-testid="skip-token-btn"
          >
            <X className="w-5 h-5" /> Skip
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


function SetupDialog({ open, onOpenChange, themes, setupTheme, setSetupTheme, setupUsePhases, setSetupUsePhases, setupStreak, setSetupStreak, setupPrize, setSetupPrize, onSave, childName, childId }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Nunito' }}>Token Program — {childName}</DialogTitle>
          <DialogDescription className="sr-only">Configure the token economy program</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2 max-h-[70vh] overflow-y-auto">
          {/* Theme */}
          <div>
            <label className="text-sm font-semibold text-slate-600 mb-2 block">Token Theme</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(THEME_PREVIEWS).map(([key, t]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSetupTheme(key)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    setupTheme === key ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`theme-${key}`}
                >
                  <p className="text-lg tracking-wider">{t.emoji.slice(0, 4)}</p>
                  <p className="text-xs font-bold text-slate-600 mt-1">{t.label}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Mode */}
          <div>
            <label className="text-sm font-semibold text-slate-600 mb-2 block">Mode</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSetupUsePhases(true)}
                className={`p-3 rounded-xl border-2 text-center ${setupUsePhases ? 'border-amber-400 bg-amber-50' : 'border-slate-200'}`}
                data-testid="mode-phases"
              >
                <p className="text-sm font-bold text-slate-700">Program Phases</p>
                <p className="text-[10px] text-slate-500">7, 5, 5, 5, 5, 5 sessions</p>
              </button>
              <button
                type="button"
                onClick={() => setSetupUsePhases(false)}
                className={`p-3 rounded-xl border-2 text-center ${!setupUsePhases ? 'border-amber-400 bg-amber-50' : 'border-slate-200'}`}
                data-testid="mode-custom"
              >
                <p className="text-sm font-bold text-slate-700">Custom Streak</p>
                <p className="text-[10px] text-slate-500">Set your own target</p>
              </button>
            </div>
          </div>

          {/* Custom streak target */}
          {!setupUsePhases && (
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-1 block">Streak Target</label>
              <Input type="number" min={2} max={32} value={setupStreak} onChange={e => setSetupStreak(parseInt(e.target.value) || 7)} className="rounded-xl" data-testid="streak-target-input" />
              <p className="text-[10px] text-slate-400 mt-1">How many tokens in a row to earn a prize</p>
            </div>
          )}

          {/* Custom Token Images */}
          {setupTheme === 'custom' && (
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Upload Token Images (5 tiers)</label>
              <p className="text-[10px] text-slate-400 mb-2">Upload images that get progressively cooler — Tier 1 is basic, Tier 5 is the ultimate reward</p>
              <div className="grid grid-cols-5 gap-2">
                {[0,1,2,3,4].map(tier => (
                  <label key={tier} className="aspect-square rounded-xl border-2 border-dashed border-slate-300 hover:border-amber-400 flex flex-col items-center justify-center cursor-pointer transition-colors bg-slate-50 overflow-hidden" data-testid={`upload-tier-${tier}`}>
                    <ImagePlus className="w-5 h-5 text-slate-400" />
                    <span className="text-[9px] text-slate-400 mt-0.5">Tier {tier + 1}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append(`tier_${tier}`, file);
                      try {
                        await api.post(`/token-economy/${childId}/custom-theme`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        toast.success(`Tier ${tier + 1} image uploaded`);
                      } catch { toast.error('Upload failed'); }
                    }} />
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Prize */}
          <div>
            <label className="text-sm font-semibold text-slate-600 mb-1 block">Prize Description</label>
            <Input value={setupPrize} onChange={e => setSetupPrize(e.target.value)} placeholder="e.g., Hot Wheels car, Sticker book" className="rounded-xl" data-testid="prize-input" />
            <p className="text-[10px] text-slate-400 mt-1">What the child earns when they complete a streak</p>
          </div>

          <Button onClick={onSave} className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold h-11 gap-2" data-testid="save-token-setup">
            <Sparkles className="w-4 h-4" /> Save Token Program
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
